import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer, LEGAL_ENTITY } from '../components/Footer';

const PrivacyPage = () => {
  const scrollToCompare = () => {
    window.location.href = '/compare';
  };

  const lastUpdated = 'January 2026';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header onStartComparing={scrollToCompare} />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-gray-50 to-orange-50/30 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600">Last updated: {lastUpdated}</p>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-gray max-w-none">
              
              {/* Introduction */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                <p className="text-gray-600 mb-4">
                  {LEGAL_ENTITY.website} is owned and operated by <strong>{LEGAL_ENTITY.name}</strong> (ABN {LEGAL_ENTITY.abn}) 
                  ("we", "us", "our"). We are committed to protecting your privacy and handling your personal 
                  information in accordance with Australian privacy laws, including the Privacy Act 1988 (Cth) 
                  and the Australian Privacy Principles.
                </p>
                <p className="text-gray-600">
                  This Privacy Policy explains how we collect, use, disclose, and protect your personal 
                  information when you use our website and services.
                </p>
              </div>

              {/* Information We Collect */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                <p className="text-gray-600 mb-4">We may collect the following types of personal information:</p>
                
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Information you provide directly:</h3>
                <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                  <li>Name (first name and last name)</li>
                  <li>Email address</li>
                  <li>Mobile phone number</li>
                  <li>Postcode/location</li>
                  <li>Information about your energy usage (e.g., monthly electricity bill)</li>
                  <li>Information about your existing solar system (if applicable)</li>
                  <li>Product preferences and comparison choices</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-800 mb-2">Information collected automatically:</h3>
                <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Device information</li>
                  <li>Pages visited and time spent on our website</li>
                  <li>Referring website</li>
                  <li>Cookies and similar technologies</li>
                </ul>
              </div>

              {/* How We Use Your Information */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
                <p className="text-gray-600 mb-4">We use your personal information to:</p>
                <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                  <li>Provide personalised comparison results and recommendations</li>
                  <li>Process your enquiries and requests</li>
                  <li>Connect you with relevant energy solution providers</li>
                  <li>Send you quotes and pricing information</li>
                  <li>Communicate with you about our services</li>
                  <li>Improve our website and services</li>
                  <li>Comply with legal obligations</li>
                  <li>Prevent fraud and ensure security</li>
                </ul>
              </div>

              {/* Sharing Your Information */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Sharing Your Information</h2>
                <p className="text-gray-600 mb-4">We may share your personal information with:</p>
                <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                  <li>Energy solution providers and installers who can assist with your enquiry</li>
                  <li>Service providers who help us operate our website and business</li>
                  <li>Professional advisers (e.g., lawyers, accountants)</li>
                  <li>Government authorities when required by law</li>
                </ul>
                <p className="text-gray-600">
                  We do not sell your personal information to third parties for marketing purposes.
                </p>
              </div>

              {/* Data Security */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
                <p className="text-gray-600 mb-4">
                  We take reasonable steps to protect your personal information from misuse, interference, 
                  loss, unauthorised access, modification, or disclosure. Our security measures include:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                  <li>Secure (HTTPS) connections for data transmission</li>
                  <li>Restricted access to personal information</li>
                  <li>Regular security assessments</li>
                  <li>Staff training on privacy and data protection</li>
                </ul>
              </div>

              {/* Cookies */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cookies and Tracking</h2>
                <p className="text-gray-600 mb-4">
                  We use cookies and similar technologies to enhance your experience on our website. 
                  Cookies help us:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                  <li>Remember your preferences</li>
                  <li>Understand how you use our website</li>
                  <li>Improve our services</li>
                  <li>Provide relevant content</li>
                </ul>
                <p className="text-gray-600">
                  You can manage your cookie preferences through your browser settings. Note that 
                  disabling cookies may affect the functionality of our website.
                </p>
              </div>

              {/* Your Rights */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Rights</h2>
                <p className="text-gray-600 mb-4">Under Australian privacy law, you have the right to:</p>
                <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your personal information (subject to legal requirements)</li>
                  <li>Opt out of marketing communications</li>
                  <li>Make a complaint about our handling of your information</li>
                </ul>
                <p className="text-gray-600">
                  To exercise any of these rights, please contact us using the details provided below.
                </p>
              </div>

              {/* Data Retention */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Data Retention</h2>
                <p className="text-gray-600">
                  We retain your personal information for as long as necessary to fulfill the purposes 
                  for which it was collected, or as required by law. When your information is no longer 
                  needed, we will securely delete or de-identify it.
                </p>
              </div>

              {/* Third Party Links */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Third Party Websites</h2>
                <p className="text-gray-600">
                  Our website may contain links to third-party websites. We are not responsible for the 
                  privacy practices of these websites. We encourage you to read the privacy policies of 
                  any websites you visit.
                </p>
              </div>

              {/* Changes to Policy */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to This Policy</h2>
                <p className="text-gray-600">
                  We may update this Privacy Policy from time to time. Changes will be posted on this 
                  page with an updated "Last updated" date. We encourage you to review this policy 
                  periodically.
                </p>
              </div>

              {/* Contact */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
                <p className="text-gray-600 mb-4">
                  If you have any questions about this Privacy Policy or wish to exercise your privacy 
                  rights, please contact us at:
                </p>
                <div className="bg-gray-50 rounded-xl p-6">
                  <p className="text-gray-700 font-medium">{LEGAL_ENTITY.name}</p>
                  <p className="text-gray-600">ABN: {LEGAL_ENTITY.abn}</p>
                  <p className="text-gray-600">Email: hello@compareyouroptions.com.au</p>
                  <p className="text-gray-600">Website: {LEGAL_ENTITY.website}</p>
                </div>
              </div>

              {/* Complaints */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Complaints</h2>
                <p className="text-gray-600 mb-4">
                  If you are not satisfied with our response to a privacy concern, you may lodge a 
                  complaint with the Office of the Australian Information Commissioner (OAIC):
                </p>
                <div className="bg-gray-50 rounded-xl p-6">
                  <p className="text-gray-700 font-medium">Office of the Australian Information Commissioner</p>
                  <p className="text-gray-600">Website: www.oaic.gov.au</p>
                  <p className="text-gray-600">Phone: 1300 363 992</p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
